import { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { subscriptionApi } from '@/services/api'
import { isAuthenticated } from '@/lib/utils'

const SubscriptionContext = createContext(null)

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [checked, setChecked] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Check subscription on mount and when auth changes
  useEffect(() => {
    // Skip subscription check on auth pages
    const authPages = ['/login', '/signup', '/email/verify']
    if (authPages.includes(location.pathname)) {
      setLoading(false)
      setChecked(true)
      return
    }
    
    checkSubscription()
  }, [location.pathname])

  const checkSubscription = async () => {
    // If not authenticated, skip subscription check
    if (!isAuthenticated()) {
      setLoading(false)
      setChecked(true)
      setSubscription(null)
      return
    }

    try {
      const response = await subscriptionApi.getCurrentSubscription()
      const subData = response.data
      
      // Check if subscription data is valid (not null/empty)
      if (!subData || subData === null) {
        // No subscription found
        setSubscription(null)
        localStorage.removeItem('subscription')
      } else {
        // Store subscription in context
        setSubscription(subData)
        
        // Store in localStorage for quick access
        localStorage.setItem('subscription', JSON.stringify({
          has_access: subData.has_access || false,
          is_completed: subData.is_completed || false,
          plan: subData.plan?.name || null,
          end_date: subData.end_date || null,
          cached_at: Date.now()
        }))
      }
      
    } catch (error) {
      console.error('Failed to fetch subscription:', error)
      
      // If 404 or subscription not found, that's okay - user has no subscription
      if (error.response?.status === 404 || error.response?.data === null) {
        setSubscription(null)
        localStorage.removeItem('subscription')
      } else {
        // Other errors - clear subscription
        setSubscription(null)
        localStorage.removeItem('subscription')
      }
    } finally {
      setLoading(false)
      setChecked(true)
    }
  }

  const hasActiveSubscription = () => {
    return subscription?.has_access && subscription?.is_completed
  }

  const needsSubscription = () => {
    return isAuthenticated() && !hasActiveSubscription()
  }

  const getCachedSubscription = () => {
    try {
      const cached = localStorage.getItem('subscription')
      if (!cached) return null
      
      const data = JSON.parse(cached)
      // Cache valid for 5 minutes
      const isValid = (Date.now() - data.cached_at) < 5 * 60 * 1000
      
      return isValid ? data : null
    } catch {
      return null
    }
  }

  const refreshSubscription = async () => {
    setLoading(true)
    await checkSubscription()
  }

  const value = {
    subscription,
    loading,
    checked,
    hasActiveSubscription,
    needsSubscription,
    getCachedSubscription,
    refreshSubscription,
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription() {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider')
  }
  return context
}
